import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import RadioGroup from "@mui/material/RadioGroup";
import type { RadioProps } from "@mui/material/Radio";
import Text from "@commons/Typography/Text";
import { FormHelperText } from "@mui/material";

type Option = { value: string | number; label: string };

type RadioInputProps = RadioProps & {
  label?: string;
  options: Option[];
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  error?: boolean;
  helperText?: string;
};

function RadioInput({
  label,
  options,
  value,
  onChange,
  name,
  error,
  helperText,
}: RadioInputProps) {
  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (String(value) === String(newValue)) {
      const emptyEvent = {
        ...e,
        target: { ...e.target, value: "" },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange?.(emptyEvent);
    } else {
      onChange?.(e);
    }
  };

  const handleOptionClick = (optionValue: string | number) => {
    if (String(value) !== String(optionValue)) return;

    const emptyEvent = {
      target: { value: "", name },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange?.(emptyEvent);
  };

  return (
    <FormControl component="fieldset" className="w-full">
      {label && (
        <div className="mb-2 font-medium">
          <Text>{label}</Text>
        </div>
      )}
      <RadioGroup
        row
        name={name}
        value={value}
        onChange={handleRadioChange}
        className="gap-x-2"
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            data-testid={`radio-${option.value}`}
            value={option.value}
            control={
              <Radio
                onClick={() => handleOptionClick(option.value)}
                icon={
                  <span className="block w-6 h-6 border-2 border-primary rounded-full bg-white shadow-md" />
                }
                checkedIcon={
                  <span className="relative block w-6 h-6 border-2 border-primary rounded-full bg-white shadow-md">
                    <span className="absolute top-1/2 left-1/2 w-3 h-3 bg-primary rounded-full -translate-x-1/2 -translate-y-1/2" />
                  </span>
                }
              />
            }
            label={<Text size="sm">{option.label}</Text>}
          />
        ))}
      </RadioGroup>
      {helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}

export default RadioInput;
